import React, { useCallback, useEffect, useState } from 'react';
import { ReactFlowProvider } from '@xyflow/react';
import styled from 'styled-components';
import IvrEditor from '../components/ivr/IvrEditor';
import Modal from '../components/common/Modal';
import Pagination from '../components/common/Pagination';
import PageHeader from '../components/common/PageHeader';
import PageState from '../components/common/PageState';
import { Button } from '../styles/common/Button.styled';
import { ConsoleInput, ConsoleLabel, FieldError, FormGroup } from '../styles/common/input.styled';
import { TableArea, Table, Th, Td, Tr, ActionButton } from '../styles/common/table.styled';
import { useApi } from '../hooks/useApi';
import { IvrResponse, PageIvrResponse } from '../types/ivr';
import { formatDate } from '../utils/format';
import { isTableRowActionTarget } from '../utils/tableInteraction';
import DebouncedSearchInput from '../components/common/DebouncedSearchInput';
import toast from 'react-hot-toast';
const Container = styled.div`display: flex; flex-direction: column; gap: 16px; width: 100%;`;
const TopBar = styled.div`display: flex; gap: 8px; input { max-width: 300px; }`;
const ProviderSection = styled.section`
  display: flex; flex-direction: column; gap: 12px;
  h3 { margin: 0; font-size: 14px; font-weight: 600; color: ${({ theme }) => theme.color.text_color_100}; }
  p { margin: 0; color: ${({ theme }) => theme.color.text_color_300}; font-size: 12px; line-height: 18px; }
`;
const KeyInputRow = styled.div`
  display: flex; align-items: flex-end; gap: 8px;
  > div { flex: 1; min-width: 0; margin-bottom: 0; }
`;

interface TtsKeyStatus {
  configured: boolean;
  masked_key: string | null;
}

interface TtsKeyProvider {
  id: string;
  label: string;
  statusPath: string;
  savePath: string;
  requestField: string;
}

const TTS_KEY_PROVIDERS: TtsKeyProvider[] = [{
  id: 'gemini',
  label: 'Gemini TTS',
  statusPath: '/group-configs/gemini-tts-key',
  savePath: '/group-configs/gemini-tts-key',
  requestField: 'gemini_tts_key',
}];

const getErrorMessage = (error: any, fallback: string) => error?.response?.data?.message || fallback;

const RoutingIvrPage = () => {
  const [view, setView] = useState<'list' | 'editor'>('list');
  const [selectedIvr, setSelectedIvr] = useState<IvrResponse | null>(null);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

  const { data: ivrPage, loading, error, request: fetchIvrs } = useApi<PageIvrResponse>();
  const { request: deleteIvrApi } = useApi();
  const { request: fetchTtsKeyStatus, loading: ttsKeyStatusLoading } = useApi<TtsKeyStatus>();
  const { request: saveTtsKey, loading: ttsKeySaving } = useApi();
  const [isTtsKeyModalOpen, setTtsKeyModalOpen] = useState(false);
  const [ttsKeyStatuses, setTtsKeyStatuses] = useState<Record<string, TtsKeyStatus>>({});
  const [ttsKeyDrafts, setTtsKeyDrafts] = useState<Record<string, string>>({});
  const [ttsKeyErrors, setTtsKeyErrors] = useState<Record<string, string>>({});
  const [ttsKeyEditing, setTtsKeyEditing] = useState<Record<string, boolean>>({});
  const [savingTtsKeyProviderId, setSavingTtsKeyProviderId] = useState<string | null>(null);
  const [ttsKeyLoadError, setTtsKeyLoadError] = useState(false);

  const loadIvrs = useCallback(async (pageNum: number, search: string) => {
    try {
      await fetchIvrs({
        method: 'GET',
        url: '/ivr',
        params: {
          pg: pageNum + 1,
          limit: 10,
          name: search || undefined,
        },
      });
    } catch (err) {
      console.error('Failed to load IVRs:', err);
    }
  }, [fetchIvrs]);

  useEffect(() => {
    if (view === 'list') {
      loadIvrs(page, appliedSearchTerm);
    }
  }, [view, page, appliedSearchTerm, loadIvrs]);

  useEffect(() => {
    fetchTtsKeyStatus({ method: 'GET', url: TTS_KEY_PROVIDERS[0].statusPath })
      .then(status => setTtsKeyStatuses({ [TTS_KEY_PROVIDERS[0].id]: status }))
      .catch(() => undefined);
  }, [fetchTtsKeyStatus]);

  const handleSearch = useCallback((nextSearchTerm?: string) => {
    setAppliedSearchTerm(typeof nextSearchTerm === 'string' ? nextSearchTerm : searchTerm);
    setPage(0);
  }, [searchTerm]);

  const startEdit = useCallback((ivr: IvrResponse) => {
    setSelectedIvr(ivr);
    setView('editor');
  }, []);

  const createNew = useCallback(() => {
    setSelectedIvr(null);
    setView('editor');
  }, []);

  const closeTtsKeyModal = useCallback(() => {
    setTtsKeyModalOpen(false);
    setTtsKeyDrafts({});
    setTtsKeyErrors({});
    setTtsKeyEditing({});
    setSavingTtsKeyProviderId(null);
    setTtsKeyLoadError(false);
  }, []);

  const openTtsKeyModal = useCallback(async () => {
    setTtsKeyModalOpen(true);
    setTtsKeyDrafts({});
    setTtsKeyErrors({});
    setTtsKeyEditing({});
    setTtsKeyLoadError(false);
    try {
      const statuses = await Promise.all(TTS_KEY_PROVIDERS.map(async provider => [
        provider.id,
        await fetchTtsKeyStatus({ method: 'GET', url: provider.statusPath }),
      ] as const));
      setTtsKeyStatuses(Object.fromEntries(statuses));
    } catch (error) {
      setTtsKeyLoadError(true);
      toast.error(getErrorMessage(error, 'TTS API KEY 설정 상태를 불러오지 못했습니다.'));
    }
  }, [fetchTtsKeyStatus]);

  const startTtsKeyEdit = useCallback((provider: TtsKeyProvider) => {
    setTtsKeyEditing(current => ({ ...current, [provider.id]: true }));
    setTtsKeyDrafts(current => ({ ...current, [provider.id]: '' }));
    setTtsKeyErrors(current => ({ ...current, [provider.id]: '' }));
  }, []);

  const saveTtsApiKey = useCallback(async (provider: TtsKeyProvider) => {
    const key = ttsKeyDrafts[provider.id]?.trim();
    if (!key) {
      setTtsKeyErrors(current => ({ ...current, [provider.id]: 'API KEY를 입력해 주세요.' }));
      return;
    }
    const action = ttsKeyStatuses[provider.id]?.configured ? '변경' : '설정';
    setSavingTtsKeyProviderId(provider.id);
    try {
      await saveTtsKey({
        method: 'PUT',
        url: provider.savePath,
        data: { [provider.requestField]: key },
      });
      setTtsKeyStatuses(current => ({ ...current, [provider.id]: { configured: true, masked_key: null } }));
      setTtsKeyDrafts(current => ({ ...current, [provider.id]: '' }));
      setTtsKeyEditing(current => ({ ...current, [provider.id]: false }));
      toast.success(`${provider.label} API KEY를 ${action}했습니다.`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'TTS API KEY 설정에 실패했습니다.'));
    } finally {
      setSavingTtsKeyProviderId(current => current === provider.id ? null : current);
    }
  }, [saveTtsKey, ttsKeyDrafts, ttsKeyStatuses]);

  const ttsKeyStatusPending = isTtsKeyModalOpen && !ttsKeyLoadError && (ttsKeyStatusLoading || Object.keys(ttsKeyStatuses).length !== TTS_KEY_PROVIDERS.length);

  const handleDelete = useCallback(async (id: number) => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      try {
        await deleteIvrApi({
          method: 'DELETE',
          url: `/ivr/${id}`,
        });
        loadIvrs(page, appliedSearchTerm);
      } catch (err) {
        alert('삭제에 실패했습니다.');
      }
    }
  }, [deleteIvrApi, loadIvrs, page, appliedSearchTerm]);

  const ttsKeyModal = isTtsKeyModalOpen && <Modal title="TTS API KEY 설정" onClose={closeTtsKeyModal} closeDisabled={ttsKeyStatusLoading || Boolean(savingTtsKeyProviderId)} width={520} footer={ttsKeyStatusPending ? undefined : <Button $variant="secondary" onClick={closeTtsKeyModal} disabled={ttsKeySaving}>닫기</Button>}>
    {ttsKeyStatusPending ? <PageState status="loading" title="TTS API KEY 설정을 불러오는 중입니다." /> : ttsKeyLoadError ? <PageState status="error" title="TTS API KEY 설정을 불러오지 못했습니다." onRetry={() => void openTtsKeyModal()} /> : TTS_KEY_PROVIDERS.map(provider => {
      const status = ttsKeyStatuses[provider.id];
      const fieldError = ttsKeyErrors[provider.id];
      const inputId = `tts-api-key-${provider.id}`;
      const isEditing = Boolean(ttsKeyEditing[provider.id]) || !status?.configured;
      const isSaving = savingTtsKeyProviderId === provider.id;
      return <ProviderSection key={provider.id}>
        <h3>{provider.label}</h3>
        <p>보안을 위해 API KEY 원문은 저장 후 다시 표시할 수 없습니다.</p>
        <KeyInputRow data-testid={`tts-key-row-${provider.id}`}>
          <FormGroup style={{ marginBottom: 0 }}>
            <ConsoleLabel htmlFor={inputId}>{provider.label} API KEY</ConsoleLabel>
            <ConsoleInput id={inputId} aria-label={`${provider.label} API KEY`} type={isEditing ? 'password' : 'text'} autoComplete="new-password" maxLength={512} value={isEditing ? ttsKeyDrafts[provider.id] || '' : status?.masked_key || '설정됨'} readOnly={!isEditing} onChange={event => {
              setTtsKeyDrafts(current => ({ ...current, [provider.id]: event.target.value }));
              setTtsKeyErrors(current => ({ ...current, [provider.id]: '' }));
            }} aria-invalid={Boolean(fieldError)} aria-describedby={fieldError ? `${inputId}-error` : undefined} placeholder={status?.configured ? `교체할 ${provider.label} API KEY를 입력하세요` : `${provider.label} API KEY를 입력하세요`} />
            {fieldError && <FieldError id={`${inputId}-error`} role="alert">{fieldError}</FieldError>}
          </FormGroup>
          {status?.configured && !isEditing ? <Button $variant="secondary" onClick={() => startTtsKeyEdit(provider)} disabled={ttsKeySaving}>수정</Button> : <Button onClick={() => void saveTtsApiKey(provider)} disabled={ttsKeySaving || !ttsKeyDrafts[provider.id]?.trim()}>{isSaving ? '저장 중...' : status?.configured ? '변경 저장' : '설정 저장'}</Button>}
        </KeyInputRow>
      </ProviderSection>;
    })}
  </Modal>;

  if (view === 'list') {
    return (
      <Container>
        <PageHeader title="IVR 설정" count={ivrPage?.totalElements || 0} actions={<><Button $variant="primary" onClick={createNew}>+ 새 IVR 생성</Button><Button $variant="secondary" onClick={() => void openTtsKeyModal()}>+ TTS API KEY</Button></>} />
        <TopBar><ConsoleInput as={DebouncedSearchInput} aria-label="IVR 명칭 검색" placeholder="IVR 명칭 검색" value={searchTerm} onChange={event => setSearchTerm(event.target.value)} onSearch={handleSearch} delay={250} /><Button onClick={() => handleSearch()}>검색</Button></TopBar>
        <TableArea style={{ width: '100%', height: 'auto' }}>
          {loading ? (
            <PageState status="loading" />
          ) : error ? <PageState status="error" title="IVR 목록을 불러오지 못했습니다" onRetry={() => loadIvrs(page, appliedSearchTerm)} /> : (
            <Table>
              <thead>
                <tr>
                  <Th>순번</Th>
                  <Th>IVR 명칭</Th>
                  <Th>버전</Th>
                  <Th>생성일시</Th>
                  <Th>수정일시</Th>
                  <Th>관리</Th>
                </tr>
              </thead>
              <tbody>
                {ivrPage?.content.map((ivr, index) => (
                  <Tr
                    key={ivr.id}
                    $clickable
                    onDoubleClick={(event) => {
                      if (isTableRowActionTarget(event.target)) return;
                      startEdit(ivr);
                    }}
                  >
                    <Td>{ivrPage.totalElements - (ivrPage.number * ivrPage.size) - index}</Td>
                    <Td style={{ fontWeight: 700 }}>{ivr.name}</Td>
                    <Td>v{ivr.graphVersion}</Td>
                    <Td>{formatDate(ivr.createdAt)}</Td>
                    <Td>{formatDate(ivr.updatedAt)}</Td>
                    <Td>
                      <ActionButton onClick={() => startEdit(ivr)}>상세 편집</ActionButton>
                      <ActionButton
                        style={{ borderColor: 'rgba(217, 45, 32, 0.45)', color: '#D92D20' }}
                        onClick={() => handleDelete(ivr.id)}
                      >
                        삭제
                      </ActionButton>
                    </Td>
                  </Tr>
                ))}
                {ivrPage?.content.length === 0 && (
                  <tr>
                    <Td colSpan={6} style={{ textAlign: 'center', padding: '40px' }}><PageState status="empty" title="등록된 IVR이 없습니다" /></Td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </TableArea>
        <Pagination
          currentPage={(ivrPage?.number || 0) + 1}
          totalPages={ivrPage?.totalPages || 1}
          onPageChange={(p) => setPage(p - 1)}
        />
        {ttsKeyModal}
      </Container>
    );
  }

  return <>
    <ReactFlowProvider>
      <IvrEditor
        selectedIvr={selectedIvr}
        setView={setView}
        setSelectedIvr={setSelectedIvr}
        geminiTtsKeyConfigured={ttsKeyStatuses.gemini?.configured}
        onOpenTtsApiKey={() => void openTtsKeyModal()}
      />
    </ReactFlowProvider>
    {ttsKeyModal}
  </>;
};

export default RoutingIvrPage;
